import React, { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { ThemeProvider } from "styled-components";
import { adminThemes } from "../styles/adminTheme";
import { loginAdmin } from "../utils/auth";

const Page = styled.main`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: clamp(24px, 5vw, 64px);
  background:
    radial-gradient(
      circle at 15% 20%,
      rgba(37, 99, 235, 0.18),
      transparent 28%
    ),
    radial-gradient(
      circle at 85% 15%,
      rgba(14, 165, 233, 0.18),
      transparent 30%
    ),
    linear-gradient(135deg, #f8fafc 0%, #eef2ff 52%, #f8fafc 100%);
`;

const LoginCard = styled.section`
  width: min(100%, 440px);
  padding: clamp(28px, 4vw, 44px);
  border: 1px solid rgba(226, 232, 240, 0.85);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.14);
  backdrop-filter: blur(24px);
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: clamp(28px, 4vw, 40px);
`;

const BrandMark = styled.div`
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  color: #ffffff;
  font-weight: 800;
  background: linear-gradient(135deg, #0f172a, #2563eb);
`;

const BrandText = styled.div`
  strong {
    display: block;
    color: #0f172a;
    font-size: 17px;
    letter-spacing: -0.02em;
  }

  span {
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
  }
`;

const Title = styled.h1`
  color: #0f172a;
  font-size: clamp(28px, 4vw, 36px);
  line-height: 1.15;
  letter-spacing: -0.04em;
  margin-bottom: 10px;
`;

const Description = styled.p`
  color: #64748b;
  font-size: clamp(14px, 2vw, 15px);
  line-height: 1.7;
  margin-bottom: clamp(28px, 4vw, 36px);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
`;

const Input = styled.input`
  width: 100%;
  height: 50px;
  padding: 0 16px;
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  color: #0f172a;
  background: #ffffff;
  font-size: 15px;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  }
`;

const SubmitButton = styled.button`
  height: 52px;
  margin-top: 8px;
  border-radius: 16px;
  color: #ffffff;
  background: linear-gradient(135deg, #0f172a, #2563eb);
  font-size: 15px;
  font-weight: 800;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
  }
`;

const HelperText = styled.p`
  margin-top: 18px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
`;

const ErrorText = styled.p`
  padding: 12px 14px;
  border-radius: 14px;
  color: #dc2626;
  background: rgba(239, 68, 68, 0.1);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("d.com@delighthds.com");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginError("");
    setLoading(true);

    try {
      await loginAdmin(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : "로그인에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={adminThemes.light}>
      <Page>
        <LoginCard>
          <Brand>
            <BrandMark>N</BrandMark>
            <BrandText>
              <strong>Neural OS</strong>
              <span>B2B Operations Console</span>
            </BrandText>
          </Brand>

          <Title>기업 전용 관리자 로그인</Title>
          <Description>
            고객사 프로젝트, 이슈, 사용량, 문서를 하나의 워크스페이스에서
            안전하게 관리하세요.
          </Description>

          <Form onSubmit={handleSubmit}>
            {loginError && <ErrorText>{loginError}</ErrorText>}
            <FieldGroup>
              이메일
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </FieldGroup>
            <FieldGroup>
              비밀번호
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </FieldGroup>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "로그인 중..." : "Neural OS 시작하기"}
            </SubmitButton>
          </Form>

          <HelperText>
            고객사 전용 계정으로 로그인하면 해당 고객사의 워크스페이스만 접근할 수 있습니다.
          </HelperText>
        </LoginCard>
      </Page>
    </ThemeProvider>
  );
};

export default LoginPage;
